import { summarizeErrors } from './member-form-errors';

import {createRecaptchaToken} from './recaptcha';

export default (config = {}) => ({
  submitting: false,

  async submitAccount(event) {
    event.preventDefault();

    if (this.submitting) {
      return;
    }

    this.submitting = true;
    this.clearErrors();
    let redirecting = false;

    try {
      // The fetch URL owns routing. Excluding Craft's native action/redirect controls keeps
      // the AJAX payload limited to CSRF, account attributes, application intent, and CAPTCHA.
      const formData = new FormData(this.$root);
      formData.delete('action');
      formData.delete('redirect');
      formData.set(
        'recaptchaToken',
        await createRecaptchaToken(config.recaptchaSiteKey, config.recaptchaAction),
      );

      const response = await fetch(config.endpoint, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      const result = await response.json();

      if (!response.ok) {
        this.renderErrors(Object.keys(result?.errors || {}).length
          ? result.errors
          : {form: [result?.message || 'We couldn’t create your account. Please try again.']});
        return;
      }

      if (typeof result?.redirect !== 'string' || result.redirect === '') {
        throw new Error('Account creation response did not include a redirect.');
      }

      redirecting = true;
      window.location.assign(result.redirect);
    } catch (error) {
      this.renderErrors({
        form: ['We couldn’t create your account. Please refresh the page and try again.'],
      });
    } finally {
      // Keep the account button disabled while the successful navigation is in progress.
      if (!redirecting) {
        this.submitting = false;
      }
    }
  },

  clearErrors() {
    this.$root.querySelector('[data-member-error-summary]')?.remove();
    // Remove only errors created by this component and detach their IDs from the controls,
    // preserving instruction IDs that the server-rendered field macros added.
    this.$root.querySelectorAll('.fui-error-messages').forEach((errorContainer) => {
      const errorId = errorContainer.id;
      if (errorId) {
        this.$root.querySelectorAll('[aria-describedby]').forEach((control) => {
          const describedBy = (control.getAttribute('aria-describedby') || '')
            .split(/\s+/)
            .filter((id) => id && id !== errorId);
          if (describedBy.length) {
            control.setAttribute('aria-describedby', describedBy.join(' '));
          } else {
            control.removeAttribute('aria-describedby');
          }
        });
      }
      errorContainer.remove();
    });
    this.$root.querySelectorAll('[aria-invalid="true"]')
      .forEach((control) => control.removeAttribute('aria-invalid'));
  },

  renderErrors(errors) {
    Object.entries(errors).forEach(([attribute, messages]) => {
      if (!Array.isArray(messages) || messages.length === 0) {
        return;
      }

      const field = attribute === 'form'
        ? null
        : this.$root.querySelector(`[data-field-handle="${CSS.escape(attribute)}"]`);
      // Field handles are the shared contract between backend error keys and the Twig macros.
      // Model-level errors use the dedicated summary target instead.
      const target = field || this.$root.querySelector('[data-account-form-errors]');
      if (!target) {
        return;
      }

      const errorId = `account-${attribute.replace(/[^a-zA-Z0-9_-]+/g, '-')}-errors`;
      const errorContainer = document.createElement('div');
      errorContainer.id = errorId;
      errorContainer.className = 'fui-error-messages';
      errorContainer.dataset.accountError = '';
      errorContainer.setAttribute('role', 'alert');
      errorContainer.setAttribute('tabindex', '-1');

      messages.forEach((message) => {
        const paragraph = document.createElement('p');
        paragraph.className = 'fui-error-message';
        paragraph.textContent = message;
        errorContainer.append(paragraph);
      });
      target.append(errorContainer);

      const control = field?.querySelector('input, select, textarea');
      if (control) {
        control.setAttribute('aria-invalid', 'true');
        const describedBy = new Set(
          (control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean),
        );
        describedBy.add(errorId);
        control.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
      }
    });

    summarizeErrors(this.$root);

    const focusTarget = this.$root.querySelector('[aria-invalid="true"], [data-account-error]');
    if (focusTarget) {
      focusTarget.focus({preventScroll: true});
      focusTarget.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  },
});
